import { useMemo, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/base/MovieCard";
import Pagination from "@/components/base/Pagination";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getHotMovies, getLatestMovies, searchMovies, sortMovies } from "@/utils/movie";

const PAGE_SIZE = 12;

export default function SearchPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const page = Math.max(1, Number(searchParams.get("page") || 1));
  const [keyword, setKeyword] = useState(q);

  const results = useMemo(() => {
    const matched = searchMovies(q);
    const hotFirst = sortMovies(matched, "hot");
    return hotFirst;
  }, [q]);

  usePageMeta({
    title: q ? `“${q}”搜索结果 - 大师兄影视` : "搜索影视 - 大师兄影视",
    description: q
      ? `大师兄影视为您找到与“${q}”相关的影视作品，共 ${results.length} 部，覆盖多种类型与题材。`
      : "在大师兄影视搜索电影、电视剧等影视资源。",
    keywords: `${q},搜索,大师兄影视,影视搜索,${q}影视`,
  });

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = results.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const recommendations = getHotMovies(6);
  const latest = getLatestMovies(6);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const next = keyword.trim();
    if (!next) return;
    navigate(`/search?q=${encodeURIComponent(next)}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handlePageChange = (p: number) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(p));
    setSearchParams(next, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const highlight = (text: string) => {
    if (!q.trim()) return text;
    const idx = text.toLowerCase().indexOf(q.trim().toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark className="bg-accent-200 text-accent-900 rounded px-0.5">{text.slice(idx, idx + q.trim().length)}</mark>
        {text.slice(idx + q.trim().length)}
      </>
    );
  };

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 pt-6 md:pt-8">
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 max-w-2xl">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-500 text-base" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="输入影视名称、导演、演员或题材关键词"
              className="w-full h-12 pl-11 pr-4 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-400 transition-colors"
            />
          </div>
          <button
            type="submit"
            className="h-12 px-7 rounded-lg bg-accent-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
          >
            搜索
          </button>
        </form>

        <div className="mt-4">
          {q ? (
            <h1 className="font-heading text-lg md:text-2xl font-extrabold text-foreground-950">
              搜索“<span className="text-primary-600">{q}</span>”的结果
            </h1>
          ) : (
            <h1 className="font-heading text-lg md:text-2xl font-extrabold text-foreground-950">搜索影视作品</h1>
          )}
          {q && (
            <p className="mt-1.5 text-sm text-foreground-600">
              共找到 <span className="text-primary-600 font-semibold">{results.length}</span> 部相关影视作品
            </p>
          )}
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-6 md:py-8">
        {!q ? (
          <div className="flex flex-col items-center justify-center py-16 rounded-lg bg-background-50 border border-background-200">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-secondary-100 mb-4">
              <i className="ri-search-eye-line text-3xl text-secondary-600" />
            </span>
            <h2 className="font-heading text-lg font-bold text-foreground-950">开始你的影视探索</h2>
            <p className="mt-1.5 text-sm text-foreground-600 text-center px-4">
              输入影视名称、题材或主演关键词，快速找到想看的内容
            </p>
          </div>
        ) : paged.length > 0 ? (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-5">
              {paged.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
            <div className="mt-8">
              <div className="flex items-center justify-center mb-4">
                <Pagination page={currentPage} totalPages={totalPages} onChange={handlePageChange} />
              </div>
              <p className="text-center text-xs text-foreground-500">
                第 {currentPage} / {totalPages} 页
              </p>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 rounded-lg bg-background-50 border border-background-200">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-secondary-100 mb-4">
              <i className="ri-emotion-sad-line text-3xl text-secondary-600" />
            </span>
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              没有找到与“{highlight(q)}”相关的影视作品
            </h2>
            <p className="mt-1.5 text-sm text-foreground-600 text-center px-4">
              试试更换关键词，或浏览下方的热门推荐
            </p>
          </div>
        )}

        {(paged.length === 0 || !q) && (
          <section className="mt-12">
            <div className="mb-5">
              <span className="inline-flex items-center gap-1.5 text-accent-600 text-xs font-semibold uppercase tracking-wider mb-1.5">
                <span className="w-4 h-0.5 bg-accent-500 rounded-full" />
                For You
              </span>
              <h2 className="font-heading text-xl font-extrabold text-foreground-950">热门影视推荐</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-5">
              {recommendations.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>

            <div className="mt-10 mb-5">
              <span className="inline-flex items-center gap-1.5 text-accent-600 text-xs font-semibold uppercase tracking-wider mb-1.5">
                <span className="w-4 h-0.5 bg-accent-500 rounded-full" />
                Latest
              </span>
              <h2 className="font-heading text-xl font-extrabold text-foreground-950">最新上线</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-5">
              {latest.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </section>
        )}
      </div>
    </SiteLayout>
  );
}